/**
 * CartCheckoutModal.jsx — Modal de paiement multi-étapes pour le panier.
 *
 * Étapes :
 *   1 — Review       : liste des articles + total + avertissement prix
 *   2 — Payment      : choix méthode (Stripe / PayPal / Crypto)
 *   3 — Processing   : formulaire de paiement selon la méthode choisie
 *   4 — Result       : COMPLETED / PARTIAL_COMPLETE / FAILED
 *
 * §12 sexies    — Croix de fermeture cerclée en haut à droite du header.
 * §12 quinquies — cards 1rem, boutons 0.625rem.
 * §12 terdecies — Aucun composant natif navigateur.
 * §12 undecies  — Casse grammaticale.
 * §12 ter       — Zéro px en dur.
 * §20           — Tokens depuis theme.js uniquement.
 * §11/§18       — Compose ModalShell + primitives daComponents.
 */
import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useAccount } from 'wagmi';

import { ModalShell } from '../ui/ModalShell';
import {
  ModalHeader,
  ModalHeaderTitle,
  ModalCloseBtn,
  ModalBody,
  ModalFooter,
  Spinner,
  FormRow,
  FormLabel,
  FormInput,
} from '../ui/ModalDA';
import { GhostButton, PrimaryButton } from '../ui/daComponents';
import { CartItemRow } from '../ui/cart/CartItemRow';
import { axiosWithToken } from '../../utils/functions/apiCallWithToken';
import { track } from '../../services/AnalyticsClient';
import { clearCart, removeFromCart } from '../../store/slices/cartSlice';
import Notification from '../../utils/functions/notification.jsx';
import { useCouponField } from '../../hooks/useCouponField';
import {COLORS, FONTS, RADII, media, FONT_SIZE, FONT_WEIGHT} from '../ui/theme';
import * as selectors from '../../store/selectors';
import { useMultiVendorCapabilities } from '../../hooks/useMultiVendorCapabilities';
import { useCheckoutFlow, STEP } from '../../hooks/useCheckoutFlow';
import { useCartValidation } from '../../hooks/useCartValidation';
import { useNowPaymentsReturn } from '../../hooks/useNowPaymentsReturn';
import { useCartUsdcPayment, USDC_PHASE } from '../../hooks/useCartUsdcPayment';
import { stripePromise } from '../../utils/stripe';
import { getPaypalScriptOptions } from '../../utils/paypalOptions';
import { buildCartItemsPayload } from '../../utils/cart/cartConstants';
import { useFiatCap } from '../../hooks/useFiatCap';

/* CheckoutForm réutilise le composant existant (Stripe PaymentElement) */
import CheckoutForm from '../../pages/Modals/checkoutForm';
/* EpicGoodbyeModal — affichée à la fermeture en mode Epic SSO (TRACK-2026-06-23-epic-cart-redirect §69 CONSERVÉ). */
import EpicGoodbyeModal from '../../pages/Modals/EpicGoodbyeModal';
import { SERVER_URL as serverURL } from '../../core/config';


/* ─── Constantes d'étapes ─────────────────────────────────────────────────────── */
/* STEP est exporté depuis useCheckoutFlow (Lot 8b §9 DRY) */

/* ─── Styled components ───────────────────────────────────────────────────────── */

const ItemsList = styled.div`
  border: 0.0625rem solid ${COLORS.line};
  border-radius: ${RADII.card};
  overflow: hidden;
  margin-bottom: 1.25rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  background: ${COLORS.bg};
  border-radius: ${RADII.card};
  margin-bottom: 1rem;
`;

const TotalLabel = styled.span`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.9375rem;
  color: ${COLORS.ink};
`;

const TotalValue = styled.span`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 1.125rem;
  color: ${COLORS.accent};
`;

const PriceWarning = styled.p`
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  color: ${COLORS.muted};
  text-align: center;
  margin: 0;
  line-height: 1.4;
`;

/* ─── Coupon (Chantier 3) ─────────────────────────────────────────────────────── */

const CouponRow = styled(FormRow)`
  margin: 0 0 1rem;
`;

const CouponHint = styled.p`
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  margin: 0.375rem 0 0;
  color: ${(p) => (p.$ok ? COLORS.ctaInk : COLORS.danger)};
`;

/* Ligne de remise affichée dans le TotalRow quand un coupon est appliqué. */
const DiscountNote = styled.span`
  font-family: ${FONTS.mono};
  font-size: 0.8125rem;
  color: ${COLORS.muted};
  text-decoration: line-through;
  margin-right: 0.5rem;
`;

/* Message d'inéligibilité affiché sous une ligne d'article quand un coupon est appliqué
 * mais que l'item n'est pas vendu par Earniverse Studio (couponEligible === false, §72).
 * Tokens DA uniquement (§20) ; aligné sur l'indentation des lignes d'article. */
const CouponIneligibleNote = styled.p`
  font-family: ${FONTS.body};
  font-size: 0.75rem;
  color: ${COLORS.muted};
  line-height: 1.4;
  margin: 0;
  padding: 0 1rem 0.625rem;
  background: ${COLORS.white};
`;

/* ─── Grille de méthodes de paiement ─────────────────────────────────────────── */

const PaymentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const PaymentOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  background: ${(p) => p.disabled ? COLORS.bg : (p.$selected ? COLORS.accentSoft : COLORS.white)};
  border: 0.0625rem solid ${(p) => p.disabled ? COLORS.line : (p.$selected ? COLORS.accent : COLORS.line)};
  border-radius: ${RADII.card};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
  text-align: left;
  transition: border-color 0.15s, background 0.15s, opacity 0.15s;
  box-shadow: ${(p) => (!p.disabled && p.$selected ? `0 0 0 0.125rem ${COLORS.accent14}` : 'none')};

  &:hover:not(:disabled) {
    border-color: ${COLORS.accent};
    background: ${COLORS.accentSoft};
  }
`;

const PaymentIcon = styled.span`
  font-size: ${FONT_SIZE.xl};
  line-height: 1;
  flex: 0 0 auto;
`;

const PaymentInfo = styled.div`
  flex: 1;
`;

const PaymentName = styled.span`
  display: block;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.9375rem;
  color: ${COLORS.ink};
`;

const PaymentDesc = styled.span`
  display: block;
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  color: ${COLORS.muted};
  margin-top: 0.125rem;
`;

/* ─── Résultat ────────────────────────────────────────────────────────────────── */

const ResultWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0 0.5rem;
  text-align: center;
`;

const ResultIcon = styled.div`
  font-size: 3rem;
  line-height: 1;
`;

const ResultTitle = styled.h3`
  font-family: ${FONTS.head};
  font-size: ${FONT_SIZE.lg};
  font-weight: ${FONT_WEIGHT.heavy};
  color: ${COLORS.ink};
  margin: 0;
`;

const ResultSubtitle = styled.p`
  font-family: ${FONTS.body};
  font-size: 0.9375rem;
  color: ${COLORS.muted};
  margin: 0;
  line-height: 1.5;
  max-width: 24rem;
`;

const PartialList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  text-align: left;
`;

const PartialItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${RADII.button};
  margin-bottom: 0.375rem;
  font-family: ${FONTS.body};
  font-size: ${FONT_SIZE.sm};
  background: ${(p) => (p.$ok ? COLORS.ctaSoft : COLORS.dangerSoft)};
  color: ${(p) => (p.$ok ? COLORS.ctaInk : COLORS.danger)};

  &::before {
    content: '${(p) => (p.$ok ? '✓' : '✕')}';
    font-weight: ${FONT_WEIGHT.bold};
    flex: 0 0 auto;
  }
`;

/* ─── Stripe loading state ────────────────────────────────────────────────────── */

const SpinnerWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 0 1rem;
`;

const SpinnerLabel = styled.p`
  font-family: ${FONTS.body};
  font-size: 0.9375rem;
  color: ${COLORS.muted};
  margin: 0;
`;

const ErrorBox = styled.div`
  background: ${COLORS.dangerSoft};
  border: 0.0625rem solid ${COLORS.dangerLine};
  border-radius: ${RADII.button};
  padding: 0.875rem 1rem;
  font-family: ${FONTS.body};
  font-size: 0.9rem;
  color: ${COLORS.danger};
  text-align: center;
  margin-bottom: 1rem;
`;

/* ─── PayPal wrapper ──────────────────────────────────────────────────────────── */

const PaypalWrap = styled.div`
  width: 100%;
  max-width: 28rem;
  margin: 1rem auto 0;
`;

/* ─── Helpers ─────────────────────────────────────────────────────────────────── */

// M-2 fix: normalize server response — completed/failed may be arrays (new) or counts (legacy)
const toItemArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'number' && v >= 0) return Array(v).fill({});
  return [];
};

/* ─── Composant principal ─────────────────────────────────────────────────────── */

/**
 * CartCheckoutModal
 *
 * @param {boolean}  isOpen
 * @param {function} onClose
 * @param {Array}    cartItems  — CartItem[]
 * @param {string}   cartTotal  — total formaté "XX.XX"
 * @param {string=}  initialPaymentMethod  — pré-sélection de méthode (Epic deep-link
 *                   `?payment=usdc` → 'crypto'). Saute directement au step PAYMENT_METHOD
 *                   avec la méthode pré-sélectionnée. Optionnel.
 */
export function CartCheckoutModal({ isOpen, onClose, cartItems, cartTotal, initialPaymentMethod }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const signedUser = useSelector(selectors.user);

  /* Product-analytics : checkout ouvert (fire-and-forget, non bloquant). */
  useEffect(() => {
    if (!isOpen) return;
    const first = (cartItems || [])[0];
    track('checkout_started', {
      asset_id: first?.itemId,
      asset_type: first?.itemType,
      meta: { items_count: (cartItems || []).length },
    });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── État du flow (Lot 8b : extrait dans useCheckoutFlow) ── */
  const {
    step, setStep,
    selectedMethod, setSelectedMethod,
    loadingIntent, setLoadingIntent,
    error, setError,
    result, setResult,
    reset: resetFlow,
  } = useCheckoutFlow();

  /* ── Validation REVIEW (Lot 8b : extrait dans useCartValidation) ──
   * `couponEligibilityByKey` (`${itemType}:${itemId}` → boolean) : un coupon ne remise que
   * les items vendus par Earniverse Studio (§72). Display-only — le serveur applique la
   * remise aux seuls items éligibles (source de vérité). Clé absente / map non chargée → éligible.
   */
  const { validationLoading, invalidItems, couponEligibilityByKey } = useCartValidation(step, STEP.REVIEW, cartItems);

  /* Éligibilité coupon d'un item : true par défaut tant que la validation n'a pas répondu
   * (la map reste vide), pour ne jamais barrer un item à tort à l'affichage. */
  const isItemCouponEligible = useCallback((item) => {
    const key = `${item.itemType}:${item.itemId}`;
    const v = couponEligibilityByKey[key];
    return v === undefined ? true : v;
  }, [couponEligibilityByKey]);

  /* ── Coupon whole-cart (Chantier 3) ──────────────────────────────────────────
   * 1 code pour tout le panier. Remise AFFICHÉE seulement — le prix réel est
   * recalculé server-side (`_calculateCartTotal` honore `couponId`). On pose le
   * `couponId` résolu sur chaque item TILE/TILEGROUP du payload (NFT = prix flat,
   * pas de coupon). §74 : hook DRY, zéro réimplémentation de fetchCoupon.
   */
  const numericCartTotal = parseFloat(cartTotal) || 0;
  const {
    couponId,
    discountPct,
    onCouponChange,
    fetching: couponFetching,
    error: couponError,
    overdue: couponOverdue,
    applied: couponApplied,
  } = useCouponField({ amount: numericCartTotal, resetSentinel: 'base' });

  /* Total affiché révisé (§72) : la remise ne porte QUE sur les items éligibles au coupon.
   *   total = Σ items (couponEligible ? priceUSD * (1 - discountPct/100) : priceUSD)
   * Items non éligibles → plein tarif. Display-only ; le serveur recalcule (source de vérité).
   * Pas de coupon appliqué → total brut inchangé. */
  // P2-7 — remise bornée [0..100] avant calcul (miroir du clamp serveur).
  const safeDiscountPct = Math.min(100, Math.max(0, Number(discountPct) || 0));
  const revisedTotal = couponApplied
    ? (cartItems || []).reduce((sum, item) => {
        const price = parseFloat(item.priceUSD || '0') || 0;
        const discounted = isItemCouponEligible(item)
          ? price * (1 - safeDiscountPct / 100)
          : price;
        return sum + discounted;
      }, 0)
    : null;

  const displayTotal = couponApplied && revisedTotal != null
    ? Number(revisedTotal).toFixed(2)
    : cartTotal;

  /* ── Auto-retrait des items indisponibles (Chantier 2) ───────────────────────
   * À la réception d'`invalidItems` non vide (validation REVIEW), on retire
   * automatiquement les items concernés du panier et on prévient via 1 toast groupé.
   * Le total se recalcule réactivement (`selectCartTotal`) côté store ; ici le parent
   * re-rend la modal avec un `cartItems`/`cartTotal` à jour.
   *
   * Garde anti-boucle : on mémorise les ids déjà traités (handledRef). Un même
   * résultat de validation ne déclenche qu'UN retrait ; la passe de validation
   * suivante (au prochain passage REVIEW) repart propre. Pas de remove→revalidate→remove.
   */
  const handledInvalidRef = React.useRef(new Set());

  /* ── Helper partagé Gate 1 / Gate 2 (§9/§74 DRY) ─────────────────────────────
   * Retire du panier les items indisponibles fournis et émet 1 toast groupé.
   * `cartItems` sert UNIQUEMENT à résoudre les noms lisibles pour le message ; si un
   * item n'y figure plus, on retombe sur `Item #<id>` (jamais de message vide).
   * Renvoie le nombre d'items effectivement retirés (0 = rien à faire).
   * Réutilisé par : (1) l'auto-retrait REVIEW (Gate 1), (2) handleInvalidItemsFromGate2.
   */
  const removeItemsAndNotify = useCallback((items) => {
    const list = Array.isArray(items) ? items : [];
    if (list.length === 0) return 0;

    const removedNames = list.map((inv) => {
      const match = cartItems.find(
        (it) => it.itemType === inv.itemType && String(it.itemId) === String(inv.itemId)
      );
      return (match && (match.name || `Item #${match.itemId}`)) || `Item #${inv.itemId}`;
    });

    list.forEach((inv) => {
      dispatch(removeFromCart({ itemType: inv.itemType, itemId: inv.itemId }));
    });

    const count = list.length;
    Notification(
      `${count} item${count > 1 ? 's were' : ' was'} removed from your cart because ` +
      `${count > 1 ? 'they are' : 'it is'} no longer available: ${removedNames.join(', ')}.`,
      false
    );
    return count;
  }, [cartItems, dispatch]);

  useEffect(() => {
    if (step !== STEP.REVIEW || !invalidItems || invalidItems.length === 0) return;

    const toRemove = invalidItems.filter((inv) => {
      const key = `${inv.itemType}:${inv.itemId}`;
      if (handledInvalidRef.current.has(key)) return false;
      handledInvalidRef.current.add(key);
      return true;
    });
    if (toRemove.length === 0) return;

    removeItemsAndNotify(toRemove);
    // cartItems/removeItemsAndNotify exclus des deps : la liste change quand le parent
    // re-rend après retrait, mais le retrait est déjà piloté par invalidItems
    // (garde handledInvalidRef). Pas de remove→revalidate→remove.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidItems, step, dispatch]);

  /* Reset de la garde quand on quitte REVIEW (nouvelle session de validation propre
   * au retour). Évite de bloquer un retrait légitime si l'item revient indispo plus tard. */
  useEffect(() => {
    if (step !== STEP.REVIEW) handledInvalidRef.current = new Set();
  }, [step]);

  /* ── Gate 2 : re-validation AU MOMENT du paiement ────────────────────────────
   * Quand l'user déclenche réellement le paiement, le backend re-valide et renvoie
   * HTTP 422 {invalid, valid} si un item est devenu indisponible depuis REVIEW
   * (vendu / verrouillé / ...). AUCUN débit n'a eu lieu (PaymentIntent/ordre NON créé).
   * On abandonne la tentative, on rejoue EXACTEMENT l'UX Gate 1 (retrait + toast groupé
   * via le helper partagé) et on ramène la modal au step REVIEW propre.
   *
   * Pré-amorçage de handledInvalidRef : on marque les items déjà retirés ici pour que
   * l'auto-retrait REVIEW (Gate 1) ne les re-traite PAS au retour (pas de 2e toast ni de
   * boucle remove→revalidate→remove), même si la passe de validation REVIEW les remonte
   * encore brièvement avant que `cartItems` ne reflète le store. */
  const handleInvalidItemsFromGate2 = useCallback((invalidFromServer) => {
    const list = Array.isArray(invalidFromServer) ? invalidFromServer : [];
    removeItemsAndNotify(list);
    const seeded = new Set();
    list.forEach((inv) => { seeded.add(`${inv.itemType}:${inv.itemId}`); });
    handledInvalidRef.current = seeded;
    setError(null);
    setStep(STEP.REVIEW);
  }, [removeItemsAndNotify, setError, setStep]);

  /* Détecte le contrat Gate 2 (422 CART_ITEMS_INVALID) sur une erreur axiosWithToken.
   * axiosWithToken décore l'Error avec {status:422, code, invalid, valid} pour ce seul cas
   * (cf. apiCallWithToken else-branch) ; tout autre `err` retourne false → handling inchangé. */
  const isGate2Invalid = useCallback((err) =>
    err?.status === 422 ||
    err?.code === 'CART_ITEMS_INVALID' ||
    Array.isArray(err?.invalid)
  , []);

  /* Un item invalide est-il encore présent dans le panier ? (garde bouton paiement) */
  const hasUnavailableItems = invalidItems.some((inv) =>
    cartItems.some(
      (it) => it.itemType === inv.itemType && String(it.itemId) === String(inv.itemId)
    )
  );

  /* ── Décorateur coupon → items (Chantier 3 + Studio §72) ──────────────────────
   * Pose le `couponId` résolu UNIQUEMENT sur les items éligibles au coupon — c.-à-d.
   * vendus par Earniverse Studio (`couponEligible === true`, fourni par /cart/validate),
   * quel que soit leur type. Les items non éligibles partent SANS couponId → le serveur
   * ne les remise pas. `buildCartItemsPayload` transmet déjà `couponId` à TOUS les endpoints
   * de paiement (Stripe / PayPal / NowPayments / USDC). Source unique : 4 méthodes.
   * Robustesse : si la map d'éligibilité n'est pas encore chargée, l'item est traité comme
   * éligible (le serveur reste la garde finale et n'appliquera la remise qu'aux items Studio).
   */
  const decorateItemsWithCoupon = useCallback((items) => {
    if (!couponId) return items;
    return (items || []).map((it) =>
      isItemCouponEligible(it) ? { ...it, couponId } : it
    );
  }, [couponId, isItemCouponEligible]);

  /* ── Stripe ── */
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [stripeCartOrderToken, setStripeCartOrderToken] = useState(null);

  /* ── PayPal ── */
  const [paypalCartOrderToken, setPaypalCartOrderToken] = useState(null);

  /* ── Epic SSO : modal "goodbye" affichée à la fermeture si l'user vient d'Epic ── */
  const [epicGoodbyeOpen, setEpicGoodbyeOpen] = useState(false);

  /* ── Vendor capabilities (Stripe/PayPal) ── */
  const {
    stripeAvailable,
    paypalAvailable,
    loading: capsLoading,
  } = useMultiVendorCapabilities(isOpen, cartItems, cartTotal);

  /* ── Plafond fiat administrable (P1-1) — valeur serveur, fallback 2000 ── */
  const { fiatCapUsd } = useFiatCap();

  /* ── USDC (Avalanche) — disponible si wallet connecté (exclut custodial) ──
   * Phase 1 (Avalanche autonome) ; SPEC multi-chaîne Phase 2 séparée.
   * §13 lock posé backend (cartUsdcService.prepare). §46 argent→asset on-chain.
   */
  const { isConnected: walletConnected } = useAccount();
  const usdcAvailable = !!walletConnected;
  const usdcPayment = useCartUsdcPayment({
    onResult: (res) => {
      // Même normalisation que Stripe/PayPal (M-2 fix / 5B-W1 fix).
      const status = res?.status || (res?.completed?.length > 0 ? 'COMPLETED' : 'FAILED');
      setResult({
        status,
        completed: toItemArray(res?.completed),
        failed: toItemArray(res?.failed),
      });
      if (status === 'COMPLETED' || status === 'PARTIAL_COMPLETE') {
        dispatch(clearCart());
      }
      setStep(STEP.RESULT);
    },
    // Gate 2 (USDC) : /cart/usdc/prepare renvoie 422 CART_ITEMS_INVALID AVANT toute
    // signature/débit on-chain. Le hook reset() puis délègue ici → même UX Gate 1 + retour REVIEW.
    onInvalidItems: handleInvalidItemsFromGate2,
  });

  /* ── Détection contexte Epic SSO ────────────────────────────────────────────
   * Marqueurs : sessionStorage._isEpic (canonique nouveau) ou localStorage._isEpic
   * (legacy, lu aussi par header.jsx) ou pathname /land_epic/ (legacy direct).
   * TRACK-2026-06-23-epic-cart-redirect.
   */
  const isEpicContext = useCallback(() => {
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('_isEpic') === 'true') return true;
    } catch (e) { /* storage unavailable */ }
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('_isEpic') === 'true') return true;
    } catch (e) { /* storage unavailable */ }
    if (typeof window !== 'undefined' && window.location && typeof window.location.pathname === 'string'
        && window.location.pathname.startsWith('/land_epic/')) return true;
    return false;
  }, []);

  /* ── Fermer effectivement (reset + onClose), partagé entre flux normal et continueBrowsing Epic ── */
  const doActualClose = useCallback(() => {
    resetFlow(); // reset step/selectedMethod/loadingIntent/error/result via useCheckoutFlow
    setStripeClientSecret(null);
    setStripePaymentIntentId(null);
    setStripeCartOrderToken(null);
    setPaypalCartOrderToken(null);
    onClose();
  }, [onClose, resetFlow]);

  /* ── Réinitialiser à la fermeture ── */
  const handleClose = useCallback(() => {
    // Epic SSO : si l'user ferme avant succès paiement (step !== RESULT), afficher EpicGoodbyeModal.
    if (isEpicContext() && step !== STEP.RESULT) {
      setEpicGoodbyeOpen(true);
      return;
    }
    doActualClose();
  }, [doActualClose, isEpicContext, step]);

  /* ── Epic SSO : nettoyer le marqueur _isEpic dès que le paiement est terminé ──
   * Sans ce cleanup, le marqueur sessionStorage._isEpic persiste après le step RESULT
   * et déclenche EpicGoodbyeModal à tort sur les achats suivants (cf. TRACK §69 Passe 2).
   */
  useEffect(() => {
    if (step === STEP.RESULT) {
      try { sessionStorage.removeItem('_isEpic'); } catch (e) { /* silent */ }
      try { localStorage.removeItem('_isEpic'); } catch (e) { /* silent */ }
    }
  }, [step]);

  /* ── Epic SSO : pré-sélection de la méthode de paiement via deep-link ──
   * Si `initialPaymentMethod` fourni (ex. `usdc` → `crypto` mapping fait côté CartPage),
   * pré-sélectionner la méthode au mount de la modal et sauter au step PAYMENT_METHOD.
   * Cf. TRACK §69 Passe 2 (régression UX vs legacy `?payment=usdc`).
   */
  useEffect(() => {
    if (isOpen && initialPaymentMethod && step === STEP.REVIEW) {
      setSelectedMethod(initialPaymentMethod);
      setStep(STEP.PAYMENT_METHOD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPaymentMethod]);

  /* ─────────────────────────────────────────────────────────────────── */
  /* ÉTAPE 2 → 3 : lancer le flow selon la méthode choisie             */
  /* ─────────────────────────────────────────────────────────────────── */

  const handleProceedWithMethod = async () => {
    if (!selectedMethod) return;
    setError(null);

    const items = buildCartItemsPayload(decorateItemsWithCoupon(cartItems));

    if (selectedMethod === 'stripe') {
      setLoadingIntent(true);
      setStep(STEP.PROCESSING);
      try {
        const res = await axiosWithToken(
          `${serverURL}/cart/create-stripe-intent`,
          'post',
          { items }
        );
        setStripeClientSecret(res.clientSecret);
        setStripePaymentIntentId(res.paymentIntentId);
        setStripeCartOrderToken(res.cartOrderToken);
      } catch (err) {
        // Gate 2 : 422 CART_ITEMS_INVALID → AUCUN PaymentIntent créé. On abandonne, on
        // ne mount JAMAIS le PaymentElement (clientSecret reste null), même UX Gate 1 + REVIEW.
        if (isGate2Invalid(err)) {
          handleInvalidItemsFromGate2(err.invalid);
          return;
        }
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Unable to initialise payment. Please try again.';
        setError(msg);
      } finally {
        setLoadingIntent(false);
      }
    } else if (selectedMethod === 'paypal') {
      setStep(STEP.PROCESSING);
    } else if (selectedMethod === 'crypto') {
      /* NowPayments — redirection externe */
      setStep(STEP.PROCESSING);
    } else if (selectedMethod === 'usdc') {
      /* USDC on-chain (Avalanche) — prepare → approve → batchBuyItems → confirm.
       * Le hook gère la machine d'état (phase) et déclenche onResult au COMPLETED/FAILED.
       */
      setStep(STEP.PROCESSING);
      usdcPayment.reset();
      // fire-and-forget — le hook met à jour phase/error en interne et appelle onResult.
      // Coupon (Chantier 3) : décorer les items TILE/TILEGROUP avec le couponId résolu
      // pour que le payload USDC (buildCartItemsPayload côté hook) le transmette aussi.
      usdcPayment.pay(decorateItemsWithCoupon(cartItems));
    }
  };

  /* ─────────────────────────────────────────────────────────────────── */
  /* STRIPE — succès CheckoutForm                                       */
  /* ─────────────────────────────────────────────────────────────────── */

  const handleStripeSuccess = async () => {
    try {
      const res = await axiosWithToken(
        `${serverURL}/cart/confirm-stripe-payment`,
        'post',
        { paymentIntentId: stripePaymentIntentId }
      );
      setResult({
        status: res.status || (res.success ? 'COMPLETED' : 'FAILED'),
        completed: toItemArray(res.completed),
        failed: toItemArray(res.failed),
      });
      if (res.success || res.status === 'COMPLETED' || res.status === 'PARTIAL_COMPLETE') {
        dispatch(clearCart());
      }
      setStep(STEP.RESULT);
    } catch (err) {
      setResult({ status: 'FAILED', completed: [], failed: [] });
      setStep(STEP.RESULT);
    }
  };

  /* ─────────────────────────────────────────────────────────────────── */
  /* PAYPAL — createOrder / onApprove                                   */
  /* ─────────────────────────────────────────────────────────────────── */

  const handlePaypalCreateOrder = async () => {
    setError(null);
    const items = buildCartItemsPayload(decorateItemsWithCoupon(cartItems));
    try {
      const res = await axiosWithToken(
        `${serverURL}/cart/create-paypal-order`,
        'post',
        { items }
      );
      setPaypalCartOrderToken(res.cartOrderToken);
      return res.orderId;
    } catch (err) {
      // Gate 2 : 422 CART_ITEMS_INVALID → AUCUN ordre PayPal créé. On retourne null (le SDK
      // PayPal annule l'ouverture de la fenêtre d'approbation, donc aucun débit) ET on rejoue
      // l'UX Gate 1 en ramenant la modal au step REVIEW.
      if (isGate2Invalid(err)) {
        handleInvalidItemsFromGate2(err.invalid);
        return null;
      }
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to create PayPal order.';
      setError(msg);
      return null;
    }
  };

  const handlePaypalApprove = async (data) => {
    setError(null);
    try {
      const res = await axiosWithToken(
        `${serverURL}/cart/capture-paypal-order`,
        'post',
        { orderId: data.orderID, cartOrderToken: paypalCartOrderToken }
      );
      setResult({
        // 5B-W1 fix: use actual CartOrder.status returned by server (handles PARTIAL_COMPLETE)
        status: res.status || (res.success ? 'COMPLETED' : 'FAILED'),
        completed: toItemArray(res.completed),
        failed: toItemArray(res.failed),
      });
      if (res.success || res.status === 'COMPLETED' || res.status === 'PARTIAL_COMPLETE') dispatch(clearCart());
      setStep(STEP.RESULT);
    } catch (err) {
      setResult({ status: 'FAILED', completed: [], failed: [] });
      setStep(STEP.RESULT);
    }
  };

  /* ─────────────────────────────────────────────────────────────────── */
  /* CRYPTO (NowPayments) — redirect                                    */
  /* ─────────────────────────────────────────────────────────────────── */

  const handleCryptoRedirect = async () => {
    setError(null);
    setLoadingIntent(true);
    const items = buildCartItemsPayload(decorateItemsWithCoupon(cartItems));
    try {
      const res = await axiosWithToken(
        `${serverURL}/cart/create-nowpayments-invoice`,
        'post',
        { items }
      );
      if (res?.invoiceUrl) {
        window.open(res.invoiceUrl, '_blank', 'noopener,noreferrer');
      } else {
        setError('Unable to generate crypto payment link. Please try again.');
      }
    } catch (err) {
      // Gate 2 : 422 CART_ITEMS_INVALID → AUCUNE invoice NowPayments créée. On n'ouvre PAS
      // l'onglet de paiement ; même UX Gate 1 + retour REVIEW.
      if (isGate2Invalid(err)) {
        handleInvalidItemsFromGate2(err.invalid);
        return;
      }
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to create crypto payment. Please try again.';
      setError(msg);
    } finally {
      setLoadingIntent(false);
    }
  };

  /* ─────────────────────────────────────────────────────────────────── */
  /* NOWPAYMENTS RETURN — détection du retour deep-link après paiement  */
  /* crypto externe. Polling jusqu'à confirmation serveur OU timeout.   */
  /* ─────────────────────────────────────────────────────────────────── */
  useNowPaymentsReturn({
    onPending: () => {
      setSelectedMethod('crypto');
      setStep(STEP.PROCESSING);
      setError(null);
    },
    onConfirmed: (res) => {
      setResult({
        status: res?.status || (res?.success ? 'COMPLETED' : 'FAILED'),
        completed: toItemArray(res?.completed),
        failed: toItemArray(res?.failed),
      });
      if (res?.success || res?.status === 'COMPLETED' || res?.status === 'PARTIAL_COMPLETE') {
        dispatch(clearCart());
      }
      setStep(STEP.RESULT);
    },
    onFailed: (payload) => {
      // §3 audit passe 2 — discrimination 4xx/5xx/AUTH/NETWORK pour message clair.
      const code = payload?.code;
      if (code === 'AUTH') {
        // Session expirée / délogué : on garde l'orderId dans l'URL, message UX.
        setError(
          payload?.message ||
          'Please sign in to view your crypto payment status. The transaction is safe and will be finalized by our system.'
        );
        setStep(STEP.PROCESSING);
        return;
      }
      if (code === 'SERVER_ERROR' || code === 'NETWORK') {
        // Erreur transitoire serveur ou réseau : on garde l'orderId, message non-définitif.
        setError(
          payload?.message ||
          'Temporary issue confirming your transaction. Please refresh the page.'
        );
        setStep(STEP.PROCESSING);
        return;
      }
      // NOT_FOUND / CLIENT_ERROR : état définitif → écran RESULT FAILED.
      setResult({
        status: 'FAILED',
        completed: [],
        failed: [],
        message: payload?.message,
      });
      setStep(STEP.RESULT);
    },
    onTimeout: () => {
      // Reste en PROCESSING avec un message — le webhook + cron prendront le relais
      // et l'user sera notifié par email + UI au prochain refresh.
      setError(
        'Crypto payment is still being confirmed by the blockchain. ' +
        'You will receive a notification once finalized.'
      );
    },
  });

  /* ─────────────────────────────────────────────────────────────────── */
  /* REVIEW — validation du panier gérée par useCartValidation (Lot 8b) */
  /* ─────────────────────────────────────────────────────────────────── */

  /* ─────────────────────────────────────────────────────────────────── */
  /* E2E bypass — court-circuiter Stripe PaymentElement en test         */
  /* ─────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (
      step === STEP.PROCESSING &&
      selectedMethod === 'stripe' &&
      stripeClientSecret &&
      !loadingIntent &&
      !error &&
      typeof window !== 'undefined' &&
      window.__E2E_STRIPE_BYPASS__
    ) {
      handleStripeSuccess();
    }
  }, [step, selectedMethod, stripeClientSecret, loadingIntent, error]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Désélectionner si la méthode choisie devient indisponible ──────────────── */
  useEffect(() => {
    if (!capsLoading) {
      if (selectedMethod === 'stripe' && !stripeAvailable) setSelectedMethod(null);
      if (selectedMethod === 'paypal' && !paypalAvailable) setSelectedMethod(null);
    }
  }, [stripeAvailable, paypalAvailable, capsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────────────────────────────────────────────────────────────────── */
  /* Titre dynamique du header selon l'étape                            */
  /* ─────────────────────────────────────────────────────────────────── */

  const headerTitles = {
    [STEP.REVIEW]: t('ReviewYourCart'),
    [STEP.PAYMENT_METHOD]: t('ChoosePaymentMethod'),
    [STEP.PROCESSING]: t('CompletePayment'),
    [STEP.RESULT]: result?.status === 'COMPLETED'
      ? t('OrderComplete')
      : result?.status === 'PARTIAL_COMPLETE'
      ? t('PartialOrder')
      : t('OrderFailed'),
  };

  /* ─────────────────────────────────────────────────────────────────── */
  /* Rendu                                                              */
  /* ─────────────────────────────────────────────────────────────────── */

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      $width="38rem"
      aria-label={headerTitles[step]}
      data-testid="cart-checkout-modal"
    >
      {/* ── Header ── */}
      <ModalHeader>
        <ModalHeaderTitle>{headerTitles[step]}</ModalHeaderTitle>
        <ModalCloseBtn
          type="button"
          aria-label={t('CloseCheckout')}
          onClick={handleClose}
        >
          ✕
        </ModalCloseBtn>
      </ModalHeader>

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 1 — Review                                              */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === STEP.REVIEW && (
        <>
          <ModalBody>
            <ItemsList>
              {cartItems.map((item) => {
                const unavailable = invalidItems.some(
                  (inv) => inv.itemType === item.itemType && String(inv.itemId) === String(item.itemId)
                );
                /* §72 : coupon appliqué + item non vendu par Studio → message d'inéligibilité
                 * sous la ligne. Hors coupon appliqué, aucun message (comportement inchangé). */
                const showIneligible = couponApplied && !isItemCouponEligible(item);
                return (
                  <React.Fragment key={`${item.itemType}-${item.itemId}`}>
                    <CartItemRow
                      item={item}
                      unavailable={unavailable}
                    />
                    {showIneligible && (
                      <CouponIneligibleNote data-testid="cart-coupon-ineligible">
                        {t('CouponItemNotEligible')}
                      </CouponIneligibleNote>
                    )}
                  </React.Fragment>
                );
              })}
            </ItemsList>

            {hasUnavailableItems && (
              <PriceWarning data-testid="cart-invalid-notice">
                Some items are no longer available and are being removed from your cart.
              </PriceWarning>
            )}

            {/* Coupon whole-cart (Chantier 3) : 1 code pour tout le panier, avant le total. */}
            <CouponRow>
              <FormLabel htmlFor="cart-coupon">{t('CouponCodeOptionalLower')}</FormLabel>
              <FormInput
                type="text"
                name="coupon"
                id="cart-coupon"
                data-testid="cart-coupon-input"
                onChange={onCouponChange}
                placeholder={t('EnterYourCouponCode')}
                disabled={couponFetching}
              />
              {couponError && (
                <CouponHint data-testid="cart-coupon-error">
                  {couponOverdue ? t('ThisCouponCodeIsExpired') : t('InvalidCouponCode')}
                </CouponHint>
              )}
              {couponApplied && !couponError && (
                <CouponHint $ok data-testid="cart-coupon-applied">
                  {t('CouponApplied')}
                </CouponHint>
              )}
            </CouponRow>

            <TotalRow>
              <TotalLabel>{t('Total')}</TotalLabel>
              <TotalValue data-testid="cart-total-value">
                {couponApplied && displayTotal !== cartTotal && (
                  <DiscountNote>${cartTotal}</DiscountNote>
                )}
                ${displayTotal}
              </TotalValue>
            </TotalRow>

            <PriceWarning>
              {t('PricesConfirmedAtCheckout')}
            </PriceWarning>

            {error && (
              <ErrorBox role="alert" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                {error}
              </ErrorBox>
            )}
          </ModalBody>

          <ModalFooter>
            <GhostButton type="button" onClick={handleClose}>
              {t('CancelBtn')}
            </GhostButton>
            <PrimaryButton
              type="button"
              data-testid="cart-choose-payment"
              disabled={validationLoading || hasUnavailableItems}
              onClick={() => {
                if (!signedUser?.id) {
                  setError('Please sign in to continue to payment.');
                  return;
                }
                if (hasUnavailableItems) return;
                setError(null);
                setStep(STEP.PAYMENT_METHOD);
              }}
            >
              {t('ChoosePaymentArrow')}
            </PrimaryButton>
          </ModalFooter>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 2 — Choix de la méthode de paiement                    */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === STEP.PAYMENT_METHOD && (
        <>
          <ModalBody>
            <PaymentGrid role="radiogroup" aria-label={t('PaymentMethod')}>
              <PaymentOption
                type="button"
                role="radio"
                data-testid="cart-payment-stripe"
                aria-checked={selectedMethod === 'stripe'}
                $selected={selectedMethod === 'stripe'}
                disabled={!stripeAvailable}
                onClick={() => { if (stripeAvailable) setSelectedMethod('stripe'); }}
              >
                <PaymentIcon aria-hidden="true">💳</PaymentIcon>
                <PaymentInfo>
                  <PaymentName>{t('CreditDebitCardCap')}</PaymentName>
                  <PaymentDesc>
                    {capsLoading
                      ? 'Checking availability…'
                      : stripeAvailable
                      ? 'Visa, Mastercard, Amex — via Stripe'
                      : parseFloat(cartTotal) > fiatCapUsd
                      ? `Not available above $${fiatCapUsd.toLocaleString('en-US')}`
                      : 'Not available for this order'}
                  </PaymentDesc>
                </PaymentInfo>
              </PaymentOption>

              <PaymentOption
                type="button"
                role="radio"
                data-testid="cart-payment-paypal"
                aria-checked={selectedMethod === 'paypal'}
                $selected={selectedMethod === 'paypal'}
                disabled={!paypalAvailable}
                onClick={() => { if (paypalAvailable) setSelectedMethod('paypal'); }}
              >
                <PaymentIcon aria-hidden="true">🅿️</PaymentIcon>
                <PaymentInfo>
                  <PaymentName>PayPal</PaymentName>
                  <PaymentDesc>
                    {capsLoading
                      ? 'Checking availability…'
                      : paypalAvailable
                      ? 'Pay securely with your PayPal account'
                      : parseFloat(cartTotal) > fiatCapUsd
                      ? `Not available above $${fiatCapUsd.toLocaleString('en-US')}`
                      : 'Not available for this order'}
                  </PaymentDesc>
                </PaymentInfo>
              </PaymentOption>

              <PaymentOption
                type="button"
                role="radio"
                aria-checked={selectedMethod === 'crypto'}
                $selected={selectedMethod === 'crypto'}
                onClick={() => setSelectedMethod('crypto')}
              >
                <PaymentIcon aria-hidden="true">🪙</PaymentIcon>
                <PaymentInfo>
                  <PaymentName>{t('Crypto')}</PaymentName>
                  <PaymentDesc>{t('PayWithCryptocurrencyViaNowPayments')}</PaymentDesc>
                </PaymentInfo>
              </PaymentOption>

              <PaymentOption
                type="button"
                role="radio"
                data-testid="cart-payment-usdc"
                aria-checked={selectedMethod === 'usdc'}
                $selected={selectedMethod === 'usdc'}
                disabled={!usdcAvailable}
                onClick={() => { if (usdcAvailable) setSelectedMethod('usdc'); }}
              >
                <PaymentIcon aria-hidden="true">⛓️</PaymentIcon>
                <PaymentInfo>
                  <PaymentName>{t('PayWithUsdc')}</PaymentName>
                  <PaymentDesc>
                    {usdcAvailable
                      ? t('PayWithUsdcDesc')
                      : t('UsdcRequiresWallet')}
                  </PaymentDesc>
                </PaymentInfo>
              </PaymentOption>
            </PaymentGrid>
          </ModalBody>

          <ModalFooter>
            <GhostButton type="button" onClick={() => setStep(STEP.REVIEW)}>
              {t('BackArrow')}
            </GhostButton>
            <PrimaryButton
              type="button"
              data-testid="cart-checkout-confirm"
              disabled={!selectedMethod}
              onClick={handleProceedWithMethod}
            >
              {t('ContinueArrow')}
            </PrimaryButton>
          </ModalFooter>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 3 — Processing : Stripe                                 */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === STEP.PROCESSING && selectedMethod === 'stripe' && (
        <>
          <ModalBody>
            {error && (
              <ErrorBox role="alert">{error}</ErrorBox>
            )}

            {/* Chargement de l'intent */}
            {loadingIntent && !error && (
              <SpinnerWrap>
                <Spinner aria-hidden="true" />
                <SpinnerLabel>{t('PreparingYourPayment')}</SpinnerLabel>
              </SpinnerWrap>
            )}

            {/* Formulaire Stripe (skipped in E2E — bypass useEffect handles the call) */}
            {!loadingIntent && !error && stripeClientSecret && (
              (typeof window !== 'undefined' && window.__E2E_STRIPE_BYPASS__)
                ? (
                  <SpinnerWrap>
                    <Spinner aria-hidden="true" />
                    <SpinnerLabel>{t('ProcessingPayment')}</SpinnerLabel>
                  </SpinnerWrap>
                ) : (
                  <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                    <CheckoutForm
                      cartMode
                      paymentIntentId={stripePaymentIntentId}
                      onSuccess={handleStripeSuccess}
                      onProcessingStart={() => {}}
                      onProcessingError={(msg) => setError(msg)}
                    />
                  </Elements>
                )
            )}
          </ModalBody>

          {/* Fermeture disponible même en processing */}
          {(error || (!loadingIntent && !stripeClientSecret)) && (
            <ModalFooter>
              <GhostButton type="button" onClick={() => { setError(null); setStep(STEP.PAYMENT_METHOD); }}>
                {t('BackArrow')}
              </GhostButton>
            </ModalFooter>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 3 — Processing : PayPal                                 */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === STEP.PROCESSING && selectedMethod === 'paypal' && (
        <>
          <ModalBody>
            {error && (
              <ErrorBox role="alert">{error}</ErrorBox>
            )}

            <PaypalWrap>
              <PayPalScriptProvider options={getPaypalScriptOptions()}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                  createOrder={handlePaypalCreateOrder}
                  onApprove={handlePaypalApprove}
                  onError={(err) => {
                    setError('PayPal encountered an error. Please try again.');
                  }}
                />
              </PayPalScriptProvider>
            </PaypalWrap>
          </ModalBody>

          <ModalFooter>
            <GhostButton type="button" onClick={() => { setError(null); setStep(STEP.PAYMENT_METHOD); }}>
              {t('BackArrow')}
            </GhostButton>
          </ModalFooter>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 3 — Processing : Crypto NowPayments                    */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === STEP.PROCESSING && selectedMethod === 'crypto' && (
        <>
          <ModalBody>
            {error && (
              <ErrorBox role="alert">{error}</ErrorBox>
            )}

            {!error && (
              <ResultWrap>
                <ResultIcon aria-hidden="true">🪙</ResultIcon>
                <ResultTitle>{t('PayWithCrypto')}</ResultTitle>
                <ResultSubtitle>
                  You will be redirected to NowPayments to complete your
                  cryptocurrency payment. Total: <strong>${cartTotal}</strong>
                </ResultSubtitle>
              </ResultWrap>
            )}
          </ModalBody>

          <ModalFooter>
            <GhostButton type="button" onClick={() => { setError(null); setStep(STEP.PAYMENT_METHOD); }}>
              {t('BackArrow')}
            </GhostButton>
            <PrimaryButton
              type="button"
              disabled={loadingIntent}
              onClick={handleCryptoRedirect}
            >
              {loadingIntent ? t('Opening') : t('PayWithCryptoArrow')}
            </PrimaryButton>
          </ModalFooter>
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 3 — Processing : USDC on-chain (Avalanche)              */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {step === STEP.PROCESSING && selectedMethod === 'usdc' && (
        <>
          <ModalBody>
            {usdcPayment.error && (
              <ErrorBox role="alert" data-testid="cart-usdc-error">{usdcPayment.error}</ErrorBox>
            )}

            {!usdcPayment.error && (
              <SpinnerWrap data-testid="cart-usdc-processing">
                <Spinner aria-hidden="true" />
                <SpinnerLabel>
                  {usdcPayment.phase === USDC_PHASE.PREPARING && t('PreparingTransaction')}
                  {usdcPayment.phase === USDC_PHASE.APPROVING && t('ApprovingUsdc')}
                  {usdcPayment.phase === USDC_PHASE.PURCHASING && t('PurchasingOnChain')}
                  {usdcPayment.phase === USDC_PHASE.CONFIRMING && t('ConfirmingTransaction')}
                  {(usdcPayment.phase === USDC_PHASE.IDLE || usdcPayment.phase === USDC_PHASE.DONE) && t('PreparingTransaction')}
                </SpinnerLabel>
              </SpinnerWrap>
            )}
          </ModalBody>

          {usdcPayment.error && (
            <ModalFooter>
              <GhostButton type="button" onClick={() => { usdcPayment.reset(); setStep(STEP.PAYMENT_METHOD); }}>
                {t('BackArrow')}
              </GhostButton>
            </ModalFooter>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ÉTAPE 4 — Result                                              */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════════════ */}
      {/* Epic SSO — Modal "goodbye" affichée à la fermeture (mode Epic) */}
      {/* ══════════════════════════════════════════════════════════════ */}
      <EpicGoodbyeModal
        open={epicGoodbyeOpen}
        onBackToCheckout={() => setEpicGoodbyeOpen(false)}
        onContinueBrowsing={() => {
          setEpicGoodbyeOpen(false);
          try { sessionStorage.removeItem('_isEpic'); } catch (e) { /* silent */ }
          try { localStorage.removeItem('_isEpic'); } catch (e) { /* silent */ }
          doActualClose();
        }}
      />

      {step === STEP.RESULT && result && (
        <>
          <ModalBody>
            <ResultWrap data-testid="cart-result">
              {/* COMPLETED */}
              {result.status === 'COMPLETED' && (
                <>
                  <ResultIcon aria-hidden="true">✅</ResultIcon>
                  <ResultTitle>{t('YourOrderIsComplete')}</ResultTitle>
                  <ResultSubtitle>
                    {result.completed.length > 0
                      ? `${result.completed.length} ${result.completed.length === 1 ? 'item' : 'items'} transferred to your account.`
                      : 'All items have been transferred to your account.'}
                  </ResultSubtitle>
                </>
              )}

              {/* PARTIAL_COMPLETE */}
              {result.status === 'PARTIAL_COMPLETE' && (
                <>
                  <ResultIcon aria-hidden="true">⚠️</ResultIcon>
                  <ResultTitle>{t('PartialOrder')}</ResultTitle>
                  <ResultSubtitle>
                    {t('PartialOrderSubtitle')}
                  </ResultSubtitle>

                  {(result.completed.length > 0 || result.failed.length > 0) && (
                    <PartialList>
                      {result.completed.map((item, i) => (
                        <PartialItem key={`ok-${i}`} $ok>
                          {item.name || item.itemId} — transferred
                        </PartialItem>
                      ))}
                      {result.failed.map((item, i) => (
                        <PartialItem key={`fail-${i}`}>
                          {item.name || item.itemId}
                          {item.reason ? ` — ${item.reason}` : ' — unavailable (refunded)'}
                        </PartialItem>
                      ))}
                    </PartialList>
                  )}
                </>
              )}

              {/* FAILED */}
              {result.status === 'FAILED' && (
                <>
                  <ResultIcon aria-hidden="true">❌</ResultIcon>
                  <ResultTitle>{t('OrderFailed')}</ResultTitle>
                  <ResultSubtitle>
                    {t('OrderFailedSubtitle')}
                  </ResultSubtitle>
                </>
              )}
            </ResultWrap>
          </ModalBody>

          <ModalFooter>
            <PrimaryButton type="button" onClick={handleClose}>
              {result.status === 'FAILED' ? t('ReturnToCart') : t('Close')}
            </PrimaryButton>
          </ModalFooter>
        </>
      )}
    </ModalShell>
  );
}

export default CartCheckoutModal;
